import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';

export type ExhibitId = 
    | 'orang-laut'
    | 'book-of-myths'
    | 'sg60'
    | 'market-traders'
    | 'early-market-traders'
    | 'badang'
    | 'peranakan-design'
    | 'peranakan-patterns'
    | 'peranakan-culture'
    | 'time-capsule';

export interface ExhibitState {
    currentExhibit: ExhibitId | null;
    exhibitStatus: Record<ExhibitId, 'available' | 'busy' | 'unknown'>;
    isSubmitting: boolean;
    isLoading: boolean;
    hasTimeout: boolean;
    downloadFailed: boolean;
    experienceLive: boolean;
    selectedPattern: string | null;
    visitedExhibits: ExhibitId[];
}

const initialState: ExhibitState = {
    currentExhibit: null,
    exhibitStatus: {
        'orang-laut': 'unknown',
        'book-of-myths': 'unknown',
        'sg60': 'unknown',
        'market-traders': 'unknown',
        'early-market-traders': 'unknown',
        'badang': 'unknown',
        'peranakan-design': 'unknown',
        'peranakan-patterns': 'unknown',
        'peranakan-culture': 'unknown',
        'time-capsule': 'unknown'
    },
    isSubmitting: false,
    isLoading: false,
    hasTimeout: false,
    downloadFailed: false,
    experienceLive: false,
    selectedPattern: null,
    visitedExhibits: [],
};

export const exhibitSlice = createSlice({
    name: 'exhibit',
    initialState,
    reducers: {
        setCurrentExhibit: (state, action: PayloadAction<ExhibitId | null>) => {
            state.currentExhibit = action.payload;
            state.hasTimeout = false;
            state.downloadFailed = false;
            state.experienceLive = false;
            
            // Add to visited exhibits if not already there
            if (action.payload && !state.visitedExhibits.includes(action.payload)) {
                state.visitedExhibits.push(action.payload);
            }
        },
        setExhibitStatus: (state, action: PayloadAction<{ id: ExhibitId, status: 'available' | 'busy' | 'unknown' }>) => {
            const { id, status } = action.payload;
            state.exhibitStatus[id] = status;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSubmitting: (state, action: PayloadAction<boolean>) => {
            state.isSubmitting = action.payload;
        },
        setTimeout: (state, action: PayloadAction<boolean>) => {
            state.hasTimeout = action.payload;
        },
        setDownloadFailed: (state, action: PayloadAction<boolean>) => {
            state.downloadFailed = action.payload;
        },
        setExperienceLive: (state, action: PayloadAction<boolean>) => {
            state.experienceLive = action.payload;
        },
        setSelectedPattern: (state, action: PayloadAction<string | null>) => {
            state.selectedPattern = action.payload;
        },
        resetExhibitState: (state) => {
            const visitedExhibits = [...state.visitedExhibits];
            Object.assign(state, { ...initialState, visitedExhibits });
        },
        // Action to completely reset exhibit state
        resetExhibitStateFull: () => initialState,
    },
    extraReducers: (builder) => {
        // Handle the PURGE action from redux-persist
        builder.addCase(PURGE, () => {
            return initialState;
        });
    },
});

export const {
    setCurrentExhibit,
    setExhibitStatus,
    setLoading,
    setSubmitting,
    setTimeout,
    setDownloadFailed,
    setExperienceLive,
    setSelectedPattern,
    resetExhibitState,
    resetExhibitStateFull
} = exhibitSlice.actions;

export default exhibitSlice.reducer; 